import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { propertyStyles as styles } from '@/tools/itemProperties/itemProperties.styles';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { getSourceManagedItemName, getSourceManagedItemOpenLink } from '@/utilities/sourceManagedItem.utilities';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { ExternalLink, Icon } from '@seeqdev/qomponents';
import type { SourceManagedItem } from '@/utilities/sourceManagedItem.types';

export type ItemScopeProps = {
  availableOutsideAnalysis: boolean;
  scopeMessage: ScopeMessage;
  canManage: boolean;
  openScopeModal: () => void;
  isLocked?: boolean;
  sourceTable?: SourceManagedItem;
  openSourceTable?: () => void;
};

export interface ScopeMessage {
  translationString: string;
  translationParameter?: Record<string, string>;
}

export const ItemScope = ({
  availableOutsideAnalysis,
  scopeMessage,
  canManage,
  openScopeModal,
  isLocked = false,
  sourceTable,
  openSourceTable,
}: ItemScopeProps) => {
  const { t } = useTranslation();
  const sourceTableName = getSourceManagedItemName(sourceTable);
  const isSourceManaged = !!sourceTableName;
  const openSourceTableLink = getSourceManagedItemOpenLink(sourceTable, openSourceTable);
  const canChangeScope = !availableOutsideAnalysis && canManage && !isLocked && !isSourceManaged;

  return (
    <div {...stylex.props(styles.scopeSection)} data-testid="itemScope">
      <div {...stylex.props(styles.scopeHeading)}>
        <label {...stylex.props(styles.fieldLabel)}>{t('PROPERTIES.SCOPE')}</label>
        <ExternalLink href={`${SUPPORT_LINK}/authorization`} variant="icon" xstyle={styles.helpLink}>
          <Icon
            icon={faQuestionCircle}
            tooltip={t('SCOPE.TOOLTIP')}
            tooltipPlacement="right"
            xstyle={styles.interactiveIcon}
          />
        </ExternalLink>
      </div>
      <div {...stylex.props(styles.scopeValue)}>
        <span className={clsx('specScopeText', stylex.props(availableOutsideAnalysis && styles.globalScope).className)}>
          {t(scopeMessage.translationString, { ...scopeMessage.translationParameter })}
        </span>
        {isSourceManaged && (
          <div {...stylex.props(styles.sourceScope)} data-testid="sourceManagedScope">
            <span>{t('SCOPE.SOURCE_TABLE_MANAGED', { tableName: sourceTableName })}</span>
            {openSourceTableLink && (
              <FakeLink
                onClick={openSourceTableLink}
                testId="scopeSourceTableLink"
                tooltipText="SCOPE.OPEN_SOURCE_TABLE_TOOLTIP"
                extraClassNames={stylex.props(styles.hoverLink).className}>
                {t('SCOPE.OPEN_SOURCE_TABLE')}
              </FakeLink>
            )}
          </div>
        )}
        {canChangeScope && (
          <div
            className={clsx('specScopeLink', stylex.props(styles.scopeAction).className)}
            data-testid="scopeChange"
            onClick={openScopeModal}>
            {t('SCOPE.CHANGE')}
          </div>
        )}
      </div>
    </div>
  );
};
